/**
 * Every stamp this app SHOWS, rendered in one time base and labelled with it.
 *
 * ⚠️ Read Critical Note #50 first. Two things went wrong here often enough to be worth one
 * shared module:
 *
 * 1. **A bare stamp is UTC, but `new Date()` does not think so.** Everything this backend
 *    stores is naive UTC (`timebase.py`), written either by SQLite's `CURRENT_TIMESTAMP` or
 *    `utc_now_stamp()` — with no `Z` on it. `new Date('2026-08-28 22:40:11')` reads that as
 *    **local** time, and `new Date('2026-08-28T22:40:11')` (ISO, no zone) is specified as
 *    local too. So the same instant moved by the viewer's offset, which on a stamp near
 *    midnight moves it across a day boundary. `parseStamp` appends the `Z` the value means.
 * 2. **An unlabelled clock reading is a guess about whose clock it is.** These stamps are
 *    audit evidence — when a snapshot was built, when an attempt was submitted, when an
 *    export was taken — so they are rendered in UTC and say so. Orcanos' own server runs on
 *    GMT, which puts this app and the source of truth in the same base rather than a
 *    viewer-dependent one.
 *
 * NOT in scope, deliberately: dates that arrive from Orcanos as strings (due dates, signature
 * dates, Updated Date, matrix and BOM column values). They are passed through untouched. They
 * are mostly date-only and their day/month order is not reliably known
 * (`training_engine.detect_date_order`, Critical Note #45), so shifting one by an offset can
 * move the calendar day and silently change an overdue or compliance verdict. Do not route
 * them through here.
 */

/** The label every stamp carries. One constant so nothing drifts. */
export const ZONE_LABEL = 'UTC';

/**
 * A stored stamp -> Date, or null when it will not read as one.
 *
 * Accepts both shapes the database holds (`2026-09-17 14:22:31` and
 * `2026-09-17T14:22:31.123456`) and treats a value with **no zone marker as UTC**, which is
 * what this backend always means. A value that already carries a zone (`Z` or `±HH:MM`) is
 * respected as-is.
 */
export function parseStamp(value) {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value).trim();
  if (!text) return null;

  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const iso = text.includes('T') ? text : text.replace(' ', 'T');
  const d = new Date(hasZone ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * A stamp rendered for a human: the viewer's locale ordering, the UTC clock, and the base
 * stated. An absent value gives `fallback`; an unreadable one is returned verbatim rather
 * than guessed at — it is never silently labelled UTC.
 */
export function formatStamp(value, { fallback = '—', dateOnly = false } = {}) {
  if (value === null || value === undefined || value === '') return fallback;
  const d = parseStamp(value);
  if (!d) return String(value);

  const opts = dateOnly
    ? { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }
    : { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' };
  const text = d.toLocaleString(undefined, opts);
  return dateOnly ? text : `${text} ${ZONE_LABEL}`;
}

/**
 * The compact form for a card or a toolbar, where the year is noise: `17 Sep, 14:22 UTC`.
 * Still labelled — a short stamp is no less a factual claim than a long one.
 */
export function formatStampShort(value, { fallback = '' } = {}) {
  if (value === null || value === undefined || value === '') return fallback;
  const d = parseStamp(value);
  if (!d) return String(value);
  const text = d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  });
  return `${text} ${ZONE_LABEL}`;
}

/**
 * `YYYYMMDD-HHMM` in UTC — for an identifier that must be stable no matter who generated it.
 * Built from the ISO string rather than the local `getHours()`-style getters, which would
 * give two viewers two different ids for the same certificate.
 */
export function stampId(value) {
  const d = parseStamp(value);
  if (!d) return null;
  const iso = d.toISOString();
  return `${iso.slice(0, 4)}${iso.slice(5, 7)}${iso.slice(8, 10)}-${iso.slice(11, 13)}${iso.slice(14, 16)}`;
}
